import { useContext, useMemo } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { compareStringsAlphabetically } from "utils/utils";
import { DSM_DESIGN_PACKAGES } from "../queries";
import { BID_PACKAGE_LIST_QUERY_MIN } from "pages/planning-workspace/queries/planning-workspace-queries";

export interface EntityOption {
  id: string;
  label: string;
}

interface RawDp {
  id: string;
  number: string | null;
  title: string | null;
  workflow_instance_milestones: { id: string; name: string | null }[];
  linkedBidPackages: { target_feature_instance: { id: string } | null }[];
}
interface RawBp {
  id: string;
  number: string | null;
  title: string | null;
}

const label = (number: string | null, title: string | null): string =>
  [number, title].filter(Boolean).join(" · ") || "(untitled)";

export interface ProjectEntities {
  designPackages: EntityOption[];
  bidPackages: EntityOption[];
  milestonesByDp: Record<string, EntityOption[]>;
  /** Bid packages linked to each design package (for contextual filtering). */
  bidPackagesByDp: Record<string, EntityOption[]>;
  nameOfDp: (id: string) => string;
  nameOfBp: (id: string) => string;
  nameOfMilestone: (id: string) => string;
  loading: boolean;
}

export function useProjectEntities(
  // Queries use the project-scoped GraphQL client. The id stays on the
  // signature so callers match the other design-scope hooks.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- call-site symmetry
  projectId: string
): ProjectEntities {
  const { gqlClientForProject } = useContext(ProjectContext);
  const { data: dpData, loading: dpLoading } = useCIQQuery<{
    feature_instance: RawDp[];
  }>(DSM_DESIGN_PACKAGES, { client: gqlClientForProject });
  const { data: bpData, loading: bpLoading } = useCIQQuery<{
    feature_instance: RawBp[];
  }>(BID_PACKAGE_LIST_QUERY_MIN, { client: gqlClientForProject });

  return useMemo(() => {
    // Natural alphanumeric sort so DP-2 / BP-2 precede *-10 (Hasura asc is lexicographic).
    const dps = [...(dpData?.feature_instance ?? [])].sort((a, b) =>
      compareStringsAlphabetically(a.number ?? "", b.number ?? "")
    );
    const bps = [...(bpData?.feature_instance ?? [])].sort((a, b) =>
      compareStringsAlphabetically(a.number ?? "", b.number ?? "")
    );
    const designPackages = dps.map((d) => ({
      id: d.id,
      label: label(d.number, d.title)
    }));
    const bidPackages = bps.map((b) => ({
      id: b.id,
      label: label(b.number, b.title)
    }));
    const milestonesByDp: Record<string, EntityOption[]> = {};
    const milestoneName: Record<string, string> = {};
    const bpName = Object.fromEntries(bidPackages.map((o) => [o.id, o.label]));
    const bpNumber = Object.fromEntries(bps.map((b) => [b.id, b.number ?? ""]));
    const bidPackagesByDp: Record<string, EntityOption[]> = {};
    dps.forEach((d) => {
      milestonesByDp[d.id] = d.workflow_instance_milestones.map((m) => {
        milestoneName[m.id] = m.name ?? "(milestone)";
        return { id: m.id, label: m.name ?? "(milestone)" };
      });
      bidPackagesByDp[d.id] = (d.linkedBidPackages ?? [])
        .map((l) => l.target_feature_instance?.id)
        .filter((id): id is string => Boolean(id))
        .map((id) => ({ id, label: bpName[id] ?? "(Bid Package)" }))
        .sort((a, b) =>
          compareStringsAlphabetically(
            bpNumber[a.id] ?? "",
            bpNumber[b.id] ?? ""
          )
        );
    });
    const dpName = Object.fromEntries(
      designPackages.map((o) => [o.id, o.label])
    );
    return {
      designPackages,
      bidPackages,
      milestonesByDp,
      bidPackagesByDp,
      nameOfDp: (id) => dpName[id] ?? "—",
      nameOfBp: (id) => bpName[id] ?? "—",
      nameOfMilestone: (id) => milestoneName[id] ?? "—",
      loading: dpLoading || bpLoading
    };
  }, [dpData, bpData, dpLoading, bpLoading]);
}
